import { styled } from '@mui/material/styles';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import HorizontalScroller from '@components/HorizontalScroller';
import {
  gutterSmAndUp,
  focusColor,
  gutterXs,
  gutterFlexible,
  maxWidth,
  primaryBlack,
} from '@style/variables.js';
import isPropValid from '@emotion/is-prop-valid';

export const StyledScroller = styled(HorizontalScroller)`
  padding: 0 1.5rem;
`;

interface SectionProps {
  order?: number;
}

export const Section = styled('section', { shouldForwardProp: isPropValid })<SectionProps>`
  position: relative;
  overflow: hidden;
  z-index: ${(props) => (props.order ? props.order : 'initial')};
`;

interface WrapperProps {
  darkGray?: boolean;
}

export const Wrapper = styled('div', { shouldForwardProp: isPropValid })<WrapperProps>`
  padding: 5rem 0rem;
  background-color: ${(props) =>
    props.darkGray ? 'var(--tra-color-surface-700)' : `${primaryBlack}`};
`;

export const ToutWrapper = styled('div')`
  max-width: ${maxWidth};
  margin: 0 auto;
`;

export const HeaderTout = styled(ToutContent)(
  ({ theme }) =>
    `
  position: relative;
  height: 120vh;
  display: flex;
  align-items: flex-end;
  padding: 4.5rem ${gutterXs} 0;
  ${theme.breakpoints.up('md')} {
    padding: 9.25rem ${gutterSmAndUp} 0;
  }
`,
);

export const HeaderTwo = styled(ToutContent)(
  ({ theme }) => `
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: 0 ${gutterXs} 2rem;
  ${theme.breakpoints.up('md')} {
    padding: 0 ${gutterSmAndUp} 3rem;
  }
`,
);

interface CardProps {
  img: string;
}

export const Card = styled('li')<CardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 21rem;
  height: 21rem;
  color: white;
  background-image: linear-gradient(to top, rgba(16, 16, 16, 1), rgba(0, 0, 0, 0)),
    url('${(props) => (props.img ? props.img : '')}');
  margin: 0 8px 24px 0;
  background-position: center;
  &:focus, &:focus-visible, &:focus-within {
    border: 3px solid ${focusColor} !important;
  },
`;

export const StyledToutContent = styled(ToutContent)`
  padding: 0 24px;
  position: absolute;
  bottom: 24px;
`;

export const VideoBlock = styled('div')`
  & div {
    position: absolute;
    top: 0;
    bottom: 0;
    height: 100vh;
    & video {
      width: 100% !important;
    }
  }
`;

export const ScrollWrapper = styled('div')`
  background-color: #101010;
`;

export const Grid = styled('ul')(
  ({ theme }) => `
  padding-left: 0;
  display: flex;
  width: fit-content;
  gap: ${gutterFlexible}vw;
  list-style: none;
  max-width: ${maxWidth};
  margin: 0 auto;
  padding: 0 ${gutterXs};
  ${theme.breakpoints.up('md')} {
    padding: 0 ${gutterSmAndUp};
  }
  @media (min-width: 600px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1000px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (min-width: 1300px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`,
);

interface AccCardProps {
  translateHeight: number;
  reverse?: boolean;
}

export const AccCard = styled('li', { shouldForwardProp: isPropValid })<AccCardProps>`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  overflow-y: hidden;
  display: grid;
  grid-template-columns: 1fr;
  &:focus-visible {
    outline: 3px solid ${focusColor} !important;
  }
  & .tout {
    transform: translateY(40px);
  }

  &:hover,
  &:focus {
    .tout {
      transform: translateY(-${(props) => props.translateHeight + 4}px );
    }
    .bottom-content {
      opacity: 1;
    }
  }

  @media (min-width: 600px) {
    width: 100%;
  }

  @media (min-width: 1000px) and (max-width: 1299px) {
    &:nth-of-type(1) {
      grid-column-start: 2;
      grid-column-end: 4;
      grid-row-start: 1;
      grid-row-end: 3;
    }

    &:nth-of-type(4) {
      grid-column-start: 1;
      grid-column-end: 3;
      grid-row-start: 3;
      grid-row-end: 5;
    }
  }

  @media (min-width: 1300px) {
    &:nth-of-type(1) {
      grid-column-start: ${(props) => (props.reverse ? 1 : 3)};
      grid-column-end: ${(props) => (props.reverse ? 3 : 5)};
      grid-row-start: 1;
      grid-row-end: 3;
    }

    &:nth-of-type(6) {
      grid-column-start: ${(props) => (props.reverse ? 3 : 1)};
      grid-column-end: ${(props) => (props.reverse ? 5 : 3)};
      grid-row-start: 3;
      grid-row-end: 5;
    }
    &:nth-of-type(7) {
      grid-column-start: ${(props) => (props.reverse ? 2 : '')};
      grid-column-end: ${(props) => (props.reverse ? 3 : '')};
    }
    &:nth-of-type(8) {
      grid-column-start: ${(props) => (props.reverse ? 1 : '')};
      grid-column-end: ${(props) => (props.reverse ? 2 : '')};
      grid-row-start: 3;
      grid-row-end: 4;
    }
    &:nth-of-type(9) {
      grid-column-start: ${(props) => (props.reverse ? 2 : '')};
      grid-column-end: ${(props) => (props.reverse ? 3 : '')};
      grid-row-start: 4;
      grid-row-end: 5;
    }

`;

export const Tout = styled(ToutContent)`
  display: flex;
  padding: 0 20px;
  flex-direction: column;
  justify-content: flex-end;
  grid-row-start: 1;
  grid-column-start: 1;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  & .bottom-content {
    position: absolute;
    padding-right: 20px;
    opacity: 0;
    transition: all 0.8s ease-out;
  }
`;

export const Image = styled('img')`
  width: 100%;
  height: auto;
  object-fit: cover;
  grid-row-start: 1;
  grid-column-start: 1;
  aspect-ratio: 1 / 1;
`;

export const Overlay = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(16, 16, 16, 1), rgba(0, 0, 0, 0));
`;
